/** `ai.walker` — the ground-walker movement module (enemies, travelling
 * pickups): constant pace along its direction, gravity, swept landings, and
 * an edge policy (`fall` off ledges, or `turn` at them). Speed multiplies by
 * an optional escalation scalar and every module-published `speedMul`.
 * `ai.hopper` adds periodic hops on top (velocity impulses the walker
 * integrates). Dynamic tag `immobile` halts horizontal travel; a push (the
 * push protocol: a conveyor, a current) carries it on top of its pace and
 * fall either way, through the same landings. A walker
 * with `turnPauseTicks` telegraphs each turn: it stops (idle) for that long,
 * then reverses. */
import { bool, id, int, num, obj, oneOf } from '../schemaBuilders.js';
import { defineModule, num as read } from '../module.js';
import { confineX, heldBy, landingSurface, pushVelocity, speedMultiplier, supportSurface, surfaces } from '../physics.js';

export type WalkerConfig = {
  speed: number; gravity: number; maxFall: number; edge: 'fall' | 'turn'; footSpan: number;
  /** Telegraph before a turn: ticks standing still before reversing. */
  turnPauseTicks: number;
  /** Escalation scalar multiplying speed (written by flow.escalation). */
  speedScalar?: string;
  /** Publish idle/move (and jump when airborne) animation states. */
  animate: boolean;
  airborneState: boolean;
};

export const walker = defineModule<WalkerConfig>({
  id: 'ai.walker', version: 1, attach: 'entity', role: 'movement', surfaceContact: true,
  summary: 'Ground walker: constant pace, gravity, landings, edge policy fall|turn (optionally telegraphed by a pause), escalation-scaled speed.',
  config: obj({ speed: num(0, 20), gravity: num(0, 120), maxFall: num(1, 80), edge: oneOf('fall', 'turn'), footSpan: num(0, 1),
    turnPauseTicks: int(0, 90), speedScalar: id(), animate: bool(), airborneState: bool() }, ['speedScalar']),
  requires: ['body.surface'], events: [], presentation: {},
  proofs: ['constant-pace', 'falls-off-edge', 'turns-at-edge', 'wraps', 'immobile-halts', 'escalation-scales-speed', 'speedMul-protocol', 'push-protocol', 'telegraphed-turn'],
  animStates: c => (c.animate ? (c.airborneState ? ['idle', 'move', 'jump'] : ['idle', 'move']) : []),
  check: c => (c.airborneState && !c.animate ? ['airborneState requires animate'] : []),
  init(ctx, e, params) {
    const dir = typeof params.dir === 'number' ? Math.sign(params.dir) : e.facing;
    Object.assign(ctx.data(e), { dir, pause: 0 });
    if (dir !== 0) e.facing = dir === 1 ? 1 : -1;
  },
  systems: {
    move(ctx, e) {
      if (!e || e.status !== 'active' || heldBy(e)) return;
      const c = ctx.config, d = ctx.data(e), world = ctx.world, list = surfaces(ctx);
      // A walker heads where it faces: placement modules (pipes) turn it by
      // setting facing; dir 0 is a standing walker.
      let dir = read(d, 'dir') === 0 ? 0 : e.facing;
      const half = (e.w / 2) * c.footSpan;
      const pace = c.speed * (c.speedScalar ? ctx.scalar(c.speedScalar, 1) : 1) * speedMultiplier(e);
      const pausing = read(d, 'pause') > 0;
      const moving = dir !== 0 && !e.tags.includes('immobile') && !pausing;
      const push = pushVelocity(ctx.signals('push'), e);
      if (pausing) {
        // Telegraphed turn: stand still, then reverse.
        d.pause = read(d, 'pause') - 1;
        if (read(d, 'pause') === 0) dir = -dir;
      } else if (moving) {
        const nextX = e.x + (dir * pace + push.vx) * ctx.dt;
        const ground = e.grounded ? list.find(s => s.entity.id === e.groundId) : undefined;
        let turn = false;
        if (c.edge === 'turn' && ground && !world.bounds.wrapX && (nextX < ground.left || nextX > ground.right)) turn = true;
        else if (c.edge === 'turn' && ground && world.bounds.wrapX && ground.entity.w < world.bounds.w
          && !supportSurface(world, list, nextX, half, e.y)) turn = true;
        else e.x = nextX;
        if (confineX(world, e) === 'clamped') turn = true;
        if (turn && c.turnPauseTicks > 0) d.pause = c.turnPauseTicks;
        else if (turn) dir = -dir;
      }
      if (!moving && push.vx !== 0) { e.x += push.vx * ctx.dt; confineX(world, e); }
      d.dir = dir;
      if (dir !== 0) e.facing = dir === 1 ? 1 : -1;
      e.vx = moving ? dir * pace : 0;
      e.vy = Math.min(e.vy + c.gravity * ctx.dt, c.maxFall);
      const sy = e.vy + push.vy, prevFeet = e.y, nextFeet = e.y + sy * ctx.dt;
      e.grounded = false; e.groundId = null;
      const land = sy >= 0 ? landingSurface(world, list, e.x, half, prevFeet, nextFeet) : null;
      if (land) { e.y = land.top; e.vy = 0; e.grounded = true; e.groundId = land.entity.id; }
      else e.y = nextFeet;
      if (e.y > world.bounds.h + 4) ctx.despawn(e, 'fell-out');
      else if (c.animate) ctx.setAnim(e, !e.grounded && c.airborneState ? 'jump' : moving ? 'move' : 'idle');
    },
  },
});

export type HopperConfig = { firstTicks: number; everyTicks: number; hopSpeed: number };

export const hopper = defineModule<HopperConfig>({
  id: 'ai.hopper', version: 1, attach: 'entity', role: 'behaviour',
  summary: 'Periodic hop impulse while grounded (fighter-fly style); the movement module integrates it.',
  config: obj({ firstTicks: int(0, 900), everyTicks: int(10, 900), hopSpeed: num(0.5, 40) }),
  requires: ['ai.walker'], events: ['hop'], presentation: { hop: { cue: 'none' } }, proofs: ['hops-on-cadence', 'no-hop-airborne'],
  animStates: () => [],
  init(ctx, e) { ctx.data(e).age = 0; },
  systems: {
    think(ctx, e) {
      if (!e || e.status !== 'active') return;
      const d = ctx.data(e), c = ctx.config;
      const age = read(d, 'age') + 1;
      d.age = age;
      if (e.grounded && !e.tags.includes('immobile') && age >= c.firstTicks && (age - c.firstTicks) % c.everyTicks === 0) {
        e.vy = -c.hopSpeed; e.grounded = false; e.groundId = null;
        ctx.emit('hop', { x: e.x, y: e.y });
      }
    },
  },
});
